import { FC, ChangeEvent, useEffect, useState } from 'react'
import { useDebounce } from 'use-debounce'
import dynamic from 'next/dynamic'
import Image from 'next/image'
import clsx from 'clsx'

const Select = dynamic(() => import('../../form-parts/select-multi/select-multi'))
const Input = dynamic(() => import('../../parts/input/input'))
const CheckBoxes = dynamic(() => import('../filter-parts/checkboxes/checkboxes'))
const Calendar = dynamic(() => import('components/filters/filter-parts/calendar/calendar'))
const FilterActive = dynamic(() => import('../filter-parts/filter-active/filter-active'))
import useOpenMobileFilters from '../useOpenMobileFilters'

import MultipleSelectType from 'constants/multiple-select-variants'
import { IOptions, IAnyObj } from 'constants/types/common.types'
import { IPCFilter } from 'constants/types/search.types'
import { A_filterUsage } from 'utils/analytics/other/filter_usage/filter_usage'
import {
  FilterTypeName,
  Key,
  FilterIconName,
  filterIconPath,
} from 'constants/search-data/search-data'

import styles from './filter-pc.module.scss'
let doOnce = false

const PCFilter: FC<IPCFilter> = ({
  parameters,
  handleParameterSet,
  handleReset,
  config,
  isBigPC,
  origin,
}) => {
  const [text, setText] = useState(parameters[config.input?.key as string] as string)
  const [debouncedText] = useDebounce(text, 1000)

  const { handleChangeFilter } = useOpenMobileFilters()

  //создает замыкание с ключом(key)
  const handleValue = (key: string) => (value: any) => {
    /* Чтобы можно было вибирать дату в календаре от большего к меньшему */
    if (key === Key.DATES && value[0] < parameters[Key.DATES][0]) {
      return handleParameterSet(key, [value[0], parameters.dates[0]])
    }
    origin && A_filterUsage(origin, key)
    handleParameterSet('page', '1')
    handleParameterSet(key, value)
  }

  const handleTextInput = (e: ChangeEvent<HTMLInputElement>) => {
    if (!doOnce) {
      origin && A_filterUsage(origin, Key.TEXT)
      doOnce = true
    }
    setText(e.target.value)
  }

  useEffect(() => {
    const parmText = parameters[config.input?.key as string] as string
    if (parmText !== text) {
      setText(parmText)
    }
  }, [parameters[config.input?.key as string]])

  useEffect(() => handleParameterSet(config.input?.key as string, debouncedText), [debouncedText])

  return (
    <aside>
      <div className={styles.filters}>
        {config.input?.key && (
          <div className={styles.filterStep}>
            <Input
              {...config.input}
              key={config.input.key}
              value={text}
              onChange={handleTextInput}
              resetButton
              onResetClick={setText}
            />
          </div>
        )}
        {!isBigPC && (
          <button className={styles.filterButton} onClick={handleChangeFilter}>
            <span className={styles.filterText}>Фильтры</span>
            <Image {...filterIconPath[FilterIconName.FILTER]} />
          </button>
        )}
        {isBigPC && (
          <>
            <div className={clsx(styles.filter, styles.filterStep)}>
              {config.main.map((item: IAnyObj) => {
                if (item.inputType === FilterTypeName.CHECKBOX) {
                  return (
                    <div className={styles.filterItem} key={item.title || item.key}>
                      {!item.noPCTitle && <p className={styles.filterTitle}>{item.title}</p>}
                      <CheckBoxes
                        datasets={item.checkboxes}
                        values={parameters[item.key] as string[]}
                        handler={handleValue(item.key)}
                        isSingleChoise={item.key === Key.NMO}
                        rowPosition={true}
                      />
                    </div>
                  )
                } else if (item.inputType === FilterTypeName.SELECT) {
                  return (
                    <div
                      className={clsx(styles.filterItem, styles.filterItemSelect)}
                      key={item.title || item.key}
                    >
                      <Select
                        options={item.themes}
                        values={parameters[item.key] as IOptions[]}
                        setValues={handleValue(item.key)}
                        title={
                          <div className={styles.numberSelected}>
                            {item.title}&nbsp;
                            <span>
                              {parameters[item.key].length
                                ? `(${parameters[item.key].length})`
                                : ''}
                            </span>
                          </div>
                        }
                        placeholder={item.placeholder}
                        modificator={MultipleSelectType.filter}
                        customStyles={item.customStyles}
                      />
                    </div>
                  )
                } else if (item.inputType === FilterTypeName.CALENDAR) {
                  return (
                    <div
                      className={clsx(styles.filterItem, styles.filterCalendar)}
                      key={item.title || item.key}
                    >
                      <Calendar
                        dates={parameters[item.key] as Date[]}
                        maxDate={item.maxDate}
                        minDate={item.minDate}
                        setDates={handleValue(item.key)}
                        isAbsolute
                        maxWidthLimiter
                      />
                    </div>
                  )
                }
              })}
            </div>
          </>
        )}
      </div>

      {isBigPC && (
        <FilterActive
          config={config}
          parameters={parameters}
          handleValue={handleValue}
          handleReset={handleReset}
        />
      )}
    </aside>
  )
}

export default PCFilter
